'use client';

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import type {
  AgentState,
  AgentAction,
  ConnectionStatus,
  ChatMessage,
  Conversation,
} from '../types/agent.types';
import { agentReducer, initialAgentState } from './agentReducer';
import { getConversation } from '../services/agentApi';
import {
  clearTriggeredToolCalls,
  markToolCallsTriggered,
} from '../components/Messages/ToolResultCard';

interface AgentContextType {
  state: AgentState;
  dispatch: React.Dispatch<AgentAction>;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setActiveConversation: (id: string) => void;
  clearConversation: () => void;
  restoreLastConversation: () => Promise<void>;
  addUserMessage: (message: ChatMessage) => void;
  setMessages: (messages: ChatMessage[]) => void;
  setConversations: (conversations: Conversation[]) => void;
  setError: (error: string | null) => void;
  toggleHistory: () => void;
}

const AgentContext = createContext<AgentContextType | null>(null);

export function useAgentContext(): AgentContextType {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentProvider');
  }
  return context;
}

export function AgentProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(agentReducer, initialAgentState);

  const togglePanel = useCallback(() => dispatch({ type: 'TOGGLE_PANEL' }), []);
  const openPanel = useCallback(() => dispatch({ type: 'OPEN_PANEL' }), []);
  const closePanel = useCallback(() => dispatch({ type: 'CLOSE_PANEL' }), []);

  const setConnectionStatus = useCallback(
    (status: ConnectionStatus) => dispatch({ type: 'SET_CONNECTION_STATUS', payload: status }),
    []
  );

  const setActiveConversation = useCallback(
    (id: string) => dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: id }),
    []
  );

  const clearConversation = useCallback(() => {
    clearTriggeredToolCalls();
    dispatch({ type: 'CLEAR_CONVERSATION' });
    try {
      localStorage.removeItem('sw-ai-last-conversation');
    } catch {
      /* ignore */
    }
  }, []);

  const restoreLastConversation = useCallback(async () => {
    try {
      const savedId = localStorage.getItem('sw-ai-last-conversation');
      if (!savedId) return;
      const data = await getConversation(savedId);
      // Pre-mark tool calls before setting messages to prevent re-firing side effects
      const toolUseIds = data.messages.flatMap(
        (m: ChatMessage) => m.toolCalls?.map(tc => tc.toolUseId) ?? []
      );
      if (toolUseIds.length > 0) markToolCallsTriggered(toolUseIds);
      dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: savedId });
      dispatch({ type: 'SET_MESSAGES', payload: data.messages });
    } catch {
      // Conversation deleted or belongs to different user — clear stale key
      try {
        localStorage.removeItem('sw-ai-last-conversation');
      } catch {
        /* ignore */
      }
    }
  }, [dispatch]);

  const addUserMessage = useCallback(
    (message: ChatMessage) => dispatch({ type: 'ADD_USER_MESSAGE', payload: message }),
    []
  );

  const setMessages = useCallback((messages: ChatMessage[]) => {
    // Pre-mark all tool calls as triggered so historical messages don't re-fire side effects
    const toolUseIds = messages.flatMap(m => m.toolCalls?.map(tc => tc.toolUseId) ?? []);
    if (toolUseIds.length > 0) markToolCallsTriggered(toolUseIds);
    dispatch({ type: 'SET_MESSAGES', payload: messages });
  }, []);

  const setConversations = useCallback(
    (conversations: Conversation[]) =>
      dispatch({ type: 'SET_CONVERSATIONS', payload: conversations }),
    []
  );

  const setError = useCallback(
    (error: string | null) => dispatch({ type: 'SET_ERROR', payload: error }),
    []
  );

  const toggleHistory = useCallback(() => dispatch({ type: 'TOGGLE_HISTORY' }), []);

  return (
    <AgentContext.Provider
      value={{
        state,
        dispatch,
        togglePanel,
        openPanel,
        closePanel,
        setConnectionStatus,
        setActiveConversation,
        clearConversation,
        restoreLastConversation,
        addUserMessage,
        setMessages,
        setConversations,
        setError,
        toggleHistory,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
}
